import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    Grid,
    Typography,
    Form,
    FormInputText,
    useAppDispatch,
    useAppSelector,
    useTheme,
} from '../../base';
import {
    Directions,
    emptyForgotPasswordForm,
    ForgotPasswordFormFields,
    forgotPasswordSchema,
    IForgotPasswordForm,
} from '../../common';
import {
    ForgotPasswordButton,
    ForgotPasswordFormWrapper,
} from './ForgotPasswordForm.styles';
import { loginActions, loginSelectors } from '../../actions';

interface Props {
    isMobile: boolean;
}

const ForgotPasswordForm = ({ isMobile }: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const isForgotPasswordLoading = useAppSelector(
        loginSelectors.getIsForgotPasswordLoading
    );

    const methods = useForm<IForgotPasswordForm>({
        resolver: yupResolver(forgotPasswordSchema(t)),
        mode: 'onSubmit',
        defaultValues: emptyForgotPasswordForm,
    });

    const onForgotPasswordSubmit = () => {
        const formData = methods.getValues();
        const payload = {
            email: formData[ForgotPasswordFormFields.email],
        };
        dispatch(loginActions.fetchForgotPassword(payload));
        methods.reset();
    };

    return (
        <ForgotPasswordFormWrapper container>
            <Form methods={methods} onSubmit={onForgotPasswordSubmit}>
                <Grid
                    item
                    xs={12}
                    pb={3.25}
                    sx={{ textAlign: isMobile ? 'center' : 'left' }}
                >
                    <Typography variant={'h4'} color={'primary'}>
                        {t('forgotPasswordPage.title')}
                    </Typography>
                </Grid>
                <Grid
                    item
                    xs={isMobile ? 12 : 8}
                    pb={4}
                    sx={{ textAlign: isMobile ? 'center' : 'left' }}
                >
                    <Typography variant={'body1'}>
                        {t('forgotPasswordPage.description')}
                    </Typography>
                </Grid>
                <Grid
                    item
                    xs={12}
                    display={'flex'}
                    justifyContent={'center'}
                    pb={4}
                >
                    <FormInputText
                        name={ForgotPasswordFormFields.email}
                        control={methods.control}
                        label={t('forgotPasswordPage.emailAddress')}
                        required
                    />
                </Grid>
                <Grid
                    item
                    xs={12}
                    display={'flex'}
                    justifyContent={
                        theme.direction === Directions.rtl ? 'left' : 'right'
                    }
                    pb={2}
                >
                    <ForgotPasswordButton
                        variant={'contained'}
                        type={'submit'}
                        loadingPosition={'start'}
                        startIcon={<></>}
                        loading={isForgotPasswordLoading}
                        fullWidth={isMobile}
                        sx={{
                            minWidth: isMobile ? 'unset' : undefined,
                        }}
                    >
                        {t('forgotPasswordPage.sendPasswordButtonText')}
                    </ForgotPasswordButton>
                </Grid>
            </Form>
        </ForgotPasswordFormWrapper>
    );
};

export default ForgotPasswordForm;
